import { useQuery } from '@tanstack/react-query';
import { MessagesSquare, PenSquare } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { CONVERSATION_TONES } from '@/hooks/conversations';
import { api, queryString } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { cn } from '@/lib/utils';
import type { ConversationListItem } from '@shared/schemas/conversation';
import type { ListResponse } from '@shared/schemas/pagination';
import { ConversationPane } from './ConversationPane';
import { StartConversationDialog } from './StartConversationDialog';

/*
 * Correspondence on a customer's record — ROADMAP.md §9.7.
 *
 * **This replaces the dashed panel that said replying would arrive with
 * conversations.** It went onto `/admin/leads` in 9.8 as a deliberate statement
 * of absence rather than a stub, because `lead_message` had been struck and a
 * temporary second thread model would have been the exact mistake it was struck
 * for. This is the thing it was waiting for.
 *
 * One component for both the lead and the client, because after conversion they
 * are the same thread — `conversation.lead_id` is kept for provenance and
 * `client_id` is added, so a customer's history reads continuously across the
 * moment somebody clicked Convert.
 */

export function CorrespondencePanel({
  leadId,
  clientId,
  emptyHint,
  contactName,
  contactEmail,
}: {
  leadId?: string;
  clientId?: string;
  emptyHint?: string;
  /**
   * Who a new thread would go to. **Both are needed for the Start button and
   * its absence is what hides it** — a caller that has not supplied them gets
   * the panel exactly as it was, which is how the invoice and back-fill screens
   * keep working unchanged.
   */
  contactName?: string;
  /** Null means they have no address. The dialog says so instead of failing on send. */
  contactEmail?: string | null;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'conversations', 'for', { leadId, clientId }],
    queryFn: () =>
      api.get<ListResponse<ConversationListItem>>(
        `/api/admin/conversations${queryString({ leadId, clientId, pageSize: 50 })}`,
      ),
    enabled: leadId !== undefined || clientId !== undefined,
  });

  const canStart = contactName !== undefined;

  /*
   * **Rendered once and used from three places**, so the dialog and the button
   * cannot drift apart between the empty state and the populated one.
   */
  const dialog = canStart ? (
    <StartConversationDialog
      open={starting}
      onOpenChange={setStarting}
      {...(clientId === undefined ? {} : { clientId })}
      {...(leadId === undefined ? {} : { leadId })}
      toName={contactName}
      toEmail={contactEmail ?? null}
      onStarted={setOpenId}
    />
  ) : null;

  const startButton = canStart ? (
    <Button
      variant="outline"
      onClick={() => {
        setStarting(true);
      }}
    >
      <PenSquare aria-hidden="true" />
      Start a conversation
    </Button>
  ) : null;

  if (query.isPending) {
    return <div className="h-24 animate-pulse rounded-lg bg-ink-100" aria-busy="true" />;
  }

  // A failed request is an error, never an empty state — CLAUDE.md §4 (P22).
  if (query.isError) {
    return (
      <div className="border-ink-200 rounded-lg border bg-white">
        <ErrorState
          title="Their messages could not be loaded"
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const threads = query.data.data;

  if (threads.length === 0) {
    return (
      <div className="border-ink-200 rounded-lg border bg-white">
        <EmptyState
          icon={MessagesSquare}
          title="No messages yet"
          hint={
            emptyHint ??
            'When you send them a message or they email in, the whole conversation appears here.'
          }
          /*
           * **The button is on the empty state, which is where it is wanted.**
           * "There is nothing here" and "here is how to change that" belong in
           * one place; a control tucked above an empty panel is one somebody
           * reads past on their way to the sentence telling them there is
           * nothing to do.
           */
          action={startButton}
        />
        {dialog}
      </div>
    );
  }

  /*
   * One thread opens straight into the reader — the common case by far, and
   * making somebody click a list of one to reach it is a click for nothing.
   */
  const selected = openId ?? (threads.length === 1 ? threads[0]!.id : null);

  return (
    <div className="flex flex-col gap-3">
      {startButton === null ? null : (
        <div className="flex justify-end">
          {startButton}
          {dialog}
        </div>
      )}
      {threads.length > 1 ? (
        <ul className="flex flex-col gap-2">
          {threads.map((thread) => (
            <li key={thread.id}>
              <button
                type="button"
                onClick={() => {
                  setOpenId(thread.id === selected ? null : thread.id);
                }}
                aria-expanded={thread.id === selected}
                className={cn(
                  'flex min-h-11 w-full flex-wrap items-center gap-2 rounded-lg border p-3 text-left',
                  thread.id === selected
                    ? 'border-bark-500 bg-honey-50/60'
                    : 'border-ink-200 hover:border-bark-300 bg-white',
                )}
              >
                <span className="text-meta text-ink-900">{thread.subject}</span>
                <StatusBadge tone={CONVERSATION_TONES[thread.status]} label={thread.statusLabel} />
                <span className="text-caption ml-auto text-ink-500">
                  {formatInstant(thread.lastMessageAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {selected === null ? null : <ConversationPane id={selected} compact />}
    </div>
  );
}
